import { CheckCircle2, XCircle, X, Loader2, RotateCw, Upload as UploadIcon } from 'lucide-react';
import { useUploader } from '../hooks/useUploader';
import { formatBytes } from '../lib/utils';

export function UploadTray() {
  const { tasks, addTasks, removeTask, retryTask, clearCompleted } = useUploader();
  const active = tasks.filter((t) => t.status === 'pending' || t.status === 'uploading');
  const done = tasks.filter((t) => t.status === 'completed');
  const failed = tasks.filter((t) => t.status === 'error');
  if (tasks.length === 0) return null;

  const overall = tasks.length ? Math.round(tasks.reduce((s, t) => s + t.progress, 0) / tasks.length) : 0;

  return (
    <div className="fixed bottom-16 lg:bottom-4 left-3 right-3 lg:left-auto lg:right-4 lg:w-80 max-h-80 lg:max-h-96 glass rounded-2xl shadow-2xl glow-violet flex flex-col overflow-hidden z-50 animate-slideUp lg:animate-scaleIn origin-bottom-right">
      <div className="h-0.5 w-full brand-gradient" />
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-border-soft bg-card/40">
        <div className="flex items-center gap-2 text-sm min-w-0">
          {active.length > 0 ? <Loader2 className="w-4 h-4 animate-spin text-primary flex-shrink-0" /> : <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0" />}
          <span className="text-text font-medium truncate">上传队列</span>
          <span className="text-[10px] text-text-muted flex-shrink-0">{active.length > 0 ? `${overall}%` : `${done.length} 完成`}</span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {done.length > 0 && (
            <button onClick={clearCompleted} className="text-xs text-text-muted hover:text-text px-2 py-1 rounded-lg hover:bg-card transition-colors">清除完成</button>
          )}
        </div>
      </div>

      <div className="overflow-y-auto flex-1 p-2 space-y-2">
        {/* 添加文件区 */}
        <label className="flex items-center justify-center gap-1.5 p-2.5 border border-dashed border-border-soft rounded-xl text-center text-xs text-text-muted hover:border-primary/60 hover:text-primary cursor-pointer transition-all">
          <UploadIcon className="w-3.5 h-3.5" /> 添加文件
          <input type="file" multiple className="hidden"
            onChange={(e) => { if (e.target.files?.length) { const parent = tasks[tasks.length - 1]?.parentId ?? null; addTasks(e.target.files, parent); } e.target.value = ''; }} />
        </label>

        {tasks.map((t) => (
          <div key={t.id} className="flex items-center gap-2 px-2 py-2 rounded-xl hover:bg-card/60 transition-colors animate-fadeIn">
            {t.status === 'completed' ? <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0 animate-scaleIn" />
              : t.status === 'error' ? <XCircle className="w-4 h-4 text-danger flex-shrink-0" />
              : <Loader2 className="w-4 h-4 text-primary animate-spin flex-shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-text truncate" title={t.file.name}>{t.file.name}</p>
                <span className="text-[10px] text-text-muted flex-shrink-0">{formatBytes(t.file.size)}</span>
              </div>
              <div className="flex items-center gap-2 mt-1.5">
                <div className="flex-1 h-1 bg-bg-secondary rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-300 ease-out ${t.status === 'error' ? 'bg-danger' : t.status === 'completed' ? 'bg-success' : 'brand-gradient'}`} style={{ width: `${t.progress}%` }} />
                </div>
                <span className="text-[10px] text-text-muted flex-shrink-0 tabular-nums">
                  {t.status === 'uploading' && t.speed ? `${formatBytes(t.speed)}/s · ` : ''}{t.progress}%
                </span>
              </div>
              {t.status === 'error' && (
                <div className="flex items-center gap-2 mt-1">
                  <button onClick={() => retryTask(t.id)} className="flex items-center gap-0.5 text-[10px] text-primary hover:underline btn-press"><RotateCw className="w-2.5 h-2.5" /> 重试</button>
                  {t.error && <p className="text-[10px] text-danger truncate flex-1">{t.error}</p>}
                </div>
              )}
            </div>
            <button onClick={() => removeTask(t.id)} className="p-1 rounded-lg text-text-muted hover:text-text hover:bg-card flex-shrink-0 btn-press transition-colors" aria-label="移除">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>

      {failed.length > 0 && <p className="px-3 py-1.5 text-[10px] text-danger border-t border-border-soft bg-danger/5">{failed.length} 个上传失败</p>}
    </div>
  );
}
